import { useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'src/components-v2/Inputs/Link/Link';

import {
  Column,
  Row,
  Container,
  Box,
  Spacer,
  Stack,
} from 'src/components-v2/Layout';
import { getFormData } from 'src/components/Forms/ContactForm/contactFormData';
import { usePageLocale } from 'src/contexts/LocaleContext';
import { MarketoForm, GdprFields } from 'src/components/Forms';
import { PhoneField } from 'src/components/Forms/PhoneField';
import { RichTextSwitch } from 'src/containers';
import { Btn } from 'src/components-v2/Inputs';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import { getBackgroundObjectPosition } from './background-position';
import UserIntentFields, { isUserIntentForm } from './UserIntentFields';
import {
  Heading,
  Disclaimer,
  Card,
  FormContainer,
  ContentStack,
  CopyStack,
  StyledBackgroundImage,
  FormFieldStack,
  CommentsField,
  CompanyFields,
  MakeTextField,
} from './UnstableContact.styled';

const backgroundFillStyles = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const getRenderableBackgroundAsset = (backgroundAsset) => {
  const isRenderableImage =
    backgroundAsset?.fields?.appearance === 'Image' &&
    backgroundAsset?.fields?.largeImage?.fields?.file?.url;
  return isRenderableImage ? backgroundAsset : undefined;
};

// The comments-field spacing is measured to whatever sits directly under the
// phone row. Intent checkboxes take that spot on form 2142, so they keep the
// gap from before that spacing landed instead of inheriting the disclaimer gap.
const phoneFieldSpacing = ({
  includeCommentsField,
  showUserIntent,
}: {
  includeCommentsField: boolean;
  showUserIntent: boolean;
}) => {
  if (showUserIntent && !includeCommentsField) {
    return {
      fieldset: {
        mb: { xs: '40px', sm: '16px !important' },
      },
      'div[class^=BaseInput-module_container]': {
        mb: 0,
      },
    };
  }

  return {
    fieldset: {
      // The select has no extra bottom margin, so this value is the gap
      // under the country control. 34px reaches the comments field. 22px
      // reaches the disclaimer text when that field is hidden: the letters
      // sit 4px below the top of their line.
      mb: includeCommentsField
        ? { xs: '40px', sm: '34px !important' }
        : { xs: '40px', sm: '22px !important' },
    },
    'div[class^=BaseInput-module_container]': {
      // On small screens the phone field is the last control. 34px matches
      // the desktop gap into the comments field. 22px is the disclaimer gap
      // when comments are hidden. The input's own 8px margin is cleared so
      // the two don't add.
      mb: includeCommentsField ? { xs: '34px', sm: 0 } : { xs: '22px', sm: 0 },
      '& input': {
        mb: { xs: '0px !important', sm: '8px' },
      },
    },
  };
};

const CmsThumbprint = dynamic(
  () => {
    return import('./CmsThumbprint');
  },
  {
    ssr: false,
  },
);

const Contact = ({ children, ...props }) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const locale = usePageLocale();
  const contactFormData = getFormData({ locale });
  const mode = useCmsMode({
    mode: props?.fields?.mode,
    backgroundColor: props?.fields?.backgroundColor ?? 'white',
  });
  const theme = useComponentTheme(mode);
  const lightTheme = useComponentTheme('light');
  const inspectorProps = useContentfulInspectorMode({
    entryId: props?.sys?.id,
  });
  const hasBackgroundAsset = Boolean(props?.fields?.backgroundAsset);
  const backgroundImage = getRenderableBackgroundAsset(
    props?.fields?.backgroundAsset,
  );
  const showUserIntent = isUserIntentForm(props?.fields?.marketoId);
  const showIntentSuccess = showUserIntent && isFormSubmitted;
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(
    backgroundImage?.fields,
  );
  const backgroundObjectPosition = getBackgroundObjectPosition(
    props?.fields?.backgroundPositionX,
    props?.fields?.backgroundPositionY,
  );
  const includeCommentsField = Boolean(props?.fields?.includeCommentsField);

  return (
    <ThemeProvider theme={theme}>
      <Box
        id='contact-form'
        className='cms-contact-form'
        component='section'
        sx={{
          background:
            theme.palette[props?.fields?.backgroundColor] ||
            'linear-gradient(101deg, #07578C 25.49%, #0E94D8 55.78%, #42F0CD 86.17%)',
          position: 'relative',
          ...(props?.fields?.styles ?? {}),
        }}
        {...inspectorProps({
          fieldId: 'internalTitle',
        })}
      >
        {backgroundImage && (
          <StyledBackgroundImage
            className='cms-contact-form-background'
            {...inspectorProps({ fieldId: 'backgroundAsset' })}
          >
            <CmsMediaSwitch
              asset={{
                ...backgroundImage.fields,
                description: '',
                isLazy: true,
                styles: {
                  ...backgroundImage.fields.styles,
                  ...backgroundFillStyles,
                  objectPosition: backgroundObjectPosition,
                },
              }}
              sources={{
                xs: makeImageQueryString({ src: smImgSrc, width: 639 }),
                sm: makeImageQueryString({ src: smImgSrc, width: 1023 }),
                md: makeImageQueryString({ src: mdImgSrc, width: 1279 }),
                lg: makeImageQueryString({ src: lgImgSrc, width: 1439 }),
                xl: makeImageQueryString({ src: lgImgSrc, width: 1920 }),
              }}
              role='presentation'
              sys={backgroundImage.sys}
            />
          </StyledBackgroundImage>
        )}
        <Spacer
          className='cms-contact-form-spacer'
          sx={{
            height: { xs: '44px', md: '112px' },
          }}
        />
        <Container
          className='cms-contact-form-container'
          sx={{ pb: { xs: '70px', md: '112px' } }}
        >
          <Row className='cms-contact-form-row'>
            <Column
              xsOffset={2}
              xs={20}
              mdOffset={1}
              md={8}
              className='cms-contact-form-column'
            >
              <ContentStack className='cms-contact-form-stack'>
                <CopyStack>
                  <RichTextSwitch
                    sys={props?.sys}
                    content={props?.fields?.leftColum}
                    nodes={{
                      h2: {
                        variant: 'h2',
                        fieldId: 'leftColum',
                      },
                      p: {
                        variant: 'p',
                        fieldId: 'leftColum',
                      },
                    }}
                  />
                </CopyStack>
                <Spacer
                  className='cms-contact-form-spacer-2'
                  sx={{
                    height: { xs: 'calc(32px - 2rem)', md: '0px' },
                    display: { xs: 'block', md: 'none' },
                  }}
                />
              </ContentStack>
            </Column>
            <Column
              xsOffset={2}
              xs={20}
              mdOffset={2}
              md={12}
              className='cms-contact-form-column-2'
            >
              <Card>
                {showIntentSuccess ? (
                  <Stack
                    className='cms-contact-form-success'
                    direction='column'
                    role='status'
                  >
                    <RichTextSwitch
                      sys={props?.sys}
                      content={props?.fields?.successMessage}
                      nodes={{
                        h3: {
                          variant: 'h3',
                          fieldId: 'successMessage',
                          sx: {
                            color: '#02294B',
                            fontFamily: theme.typography.plaidSans,
                            fontSize: {
                              xs: '2.0rem',
                              sm: '2.4rem',
                              md: '3.0rem',
                            },
                            fontWeight: 500,
                            lineHeight: '120%',
                            letterSpacing: '-0.6px',
                            margin: 0,
                          },
                        },
                        p: {
                          variant: 'p',
                          fieldId: 'successMessage',
                          sx: {
                            color: '#02294B',
                            fontFamily: theme.typography.plaidSans,
                            fontSize: '1.6rem',
                            fontWeight: 400,
                            lineHeight: '150%',
                            margin: '16px 0 0',
                          },
                        },
                      }}
                    />
                  </Stack>
                ) : (
                  <>
                    <Heading
                      className='cms-contact-form-heading'
                      {...inspectorProps({ fieldId: 'formTitle' })}
                    >
                      {props?.fields?.formTitle}
                    </Heading>
                    <Spacer
                      className='cms-contact-form-spacer-3'
                      sx={{ height: { xs: '32px' } }}
                    />
                    <MarketoForm
                      marketoKey={props?.fields?.marketoId ?? 2007}
                      trackingId='CONTACT_FORM'
                      locale={locale}
                      shouldForward={!showUserIntent}
                      onSubmit={() => {
                        if (showUserIntent) {
                          setIsFormSubmitted(true);
                        }
                      }}
                      render={({ control, register, errors, setValue }) => {
                        return (
                          <FormContainer
                            className='cms-contact-form-box'
                            errors={errors}
                          >
                            <Stack
                              direction='column'
                              gap={{ xs: 0, sm: 0 }}
                              className='cms-contact-form-stack'
                            >
                              <FormFieldStack className='cms-contact-form-stack-first-name-last-name'>
                                {MakeTextField({
                                  contactFormData,
                                  field: 'firstName',
                                  errors,
                                  register,
                                })}
                                {MakeTextField({
                                  contactFormData,
                                  field: 'lastName',
                                  errors,
                                  register,
                                })}
                              </FormFieldStack>
                              <CompanyFields
                                contactFormData={contactFormData}
                                errors={errors}
                                locale={locale}
                                register={register}
                                control={control}
                                setValue={setValue}
                              />
                              <FormFieldStack
                                className='cms-contact-form-stack-gdpr-phone'
                                sx={phoneFieldSpacing({
                                  includeCommentsField,
                                  showUserIntent,
                                })}
                              >
                                <GdprFields
                                  control={control}
                                  errors={errors}
                                  register={register}
                                  setValue={setValue}
                                />
                                <PhoneField
                                  className='cms-contact-form-text-input'
                                  data={contactFormData.phone}
                                  errors={errors}
                                  register={register}
                                />
                              </FormFieldStack>
                              {includeCommentsField && (
                                <CommentsField
                                  contactFormData={contactFormData}
                                  errors={errors}
                                  register={register}
                                  placeholder={
                                    props?.fields?.commentsFieldPlaceholder
                                  }
                                  inspectorProps={inspectorProps({
                                    fieldId: 'commentsFieldPlaceholder',
                                  })}
                                  followedByIntent={showUserIntent}
                                />
                              )}
                            </Stack>
                            {showUserIntent && (
                              <UserIntentFields setValue={setValue} />
                            )}
                            <Disclaimer
                              className='cms-contact-form-disclaimer'
                              sx={
                                showUserIntent
                                  ? { mb: { xs: '20px', sm: '16px' } }
                                  : {
                                      // 24px of margin leaves 26px between the
                                      // disclaimer text and the button, matching
                                      // the gap above the disclaimer.
                                      mb: '24px',
                                    }
                              }
                            >
                              By submitting this form, I confirm that I have
                              read and understood{' '}
                              <Link href='/legal/#privacy-statement'>
                                <a>Plaid's Privacy Statement</a>
                              </Link>
                              .
                            </Disclaimer>
                            <Stack
                              className='cms-contact-form-stack-6'
                              direction='row'
                              justifyContent={{
                                xs: 'center',
                                sm: 'flex-start',
                              }}
                            >
                              <ThemeProvider theme={lightTheme}>
                                <Btn
                                  className='cms-contact-form-btn'
                                  variant={props?.fields?.button?.fields?.appearance.toLowerCase()}
                                  type='submit'
                                  sys={props?.fields?.button?.sys}
                                >
                                  {props?.fields?.button?.fields?.text}
                                </Btn>
                              </ThemeProvider>
                            </Stack>
                          </FormContainer>
                        );
                      }}
                    />
                  </>
                )}
              </Card>
            </Column>
          </Row>
          {children}
        </Container>
        {!hasBackgroundAsset && (
          <CmsThumbprint key='contact' color={props?.fields?.guillocheColor} />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Contact;
